/**
 * Переключатель контекста: где я сейчас и на что переключаюсь.
 *
 * Спека §5 делает несущими три уровня — организация → пространство → бренд, — и
 * переключатель раньше показывал все три подряд: «Кардиджитал · Все пространства
 * · Автодом». Иерархия в такой строке не читается ни у кого: у компании с одним
 * брендом два первых звена всегда одинаковые и ничего не сообщают, а у агентства
 * с пятнадцатью брендами выбор шёл в два приёма — сперва пространство, потом
 * бренд, — и «Все пространства» было отдельным состоянием, в котором половина
 * экранов ничего не показывает.
 *
 * Поэтому здесь ровно один предмет выбора — **бренд**. Всё остальное стало
 * подписью к нему:
 *
 * 1. **В кнопке — название бренда**, а под ним мелким то, что отличает его от
 *    соседнего: клиент агентства и домен. Агентство обязано видеть, в чьих
 *    данных находится (app-kit.md §1), — эта строка и есть ответ.
 * 2. **В списке — все бренды сразу**, сгруппированные по клиенту. Группа
 *    появляется, только когда клиентов больше одного: единственный заголовок
 *    над единственной группой — это слово, за которым ничего не выбирается.
 * 3. **Поиск** включается с восьми брендов: до этого список читается глазами,
 *    после — уже нет.
 * 4. **Слово «пространство» у агентства заменено на «клиент»**: клиент — это то,
 *    чем агентство и торгует, а «пространство» в его кабинете лишний термин.
 *
 * Смена организации — не то же самое, что смена бренда: она идёт через
 * `POST /auth/context` и перевыпускает токен, потому что арендатор живёт в
 * токене. Поэтому у неё бывает ошибка, и её видно. Выбирать организацию есть из
 * чего только при нескольких членствах — иначе группы нет вовсе.
 */

import { useState } from 'react';
import { Menu, type MenuGroup, type MenuItem } from './Menu';
import { useAccess, useSessionState } from '../session';
import { useScope, type Brand } from '../scope';
import { ROLE_NAMES } from '../api/access';
import { humanMessage } from '../api/problem';
import { ApiError } from '../api/problem';
import { domainProse } from '@shared/src/format';
import { useT } from '../i18n/react';
import type { Session } from '../api/auth';

/** С этого числа брендов список перестаёт читаться глазами и нужен поиск. */
const SEARCH_FROM = 8;

/** Пространство клиента агентства называется так, как его назвали для клиента. */
function workspaceTitle(name: string, clientName: string | null | undefined): string {
  return clientName ?? name;
}

export function ContextSwitcher({ session }: { session: Session }) {
  const t = useT();
  const { switchOrganization } = useSessionState();
  const { role } = useAccess();
  const scope = useScope();
  const [error, setError] = useState<unknown>(null);

  const workspaces = scope.state.kind === 'ready' ? scope.state.workspaces : [];
  // Только живые: архивный бренд выбирать нечего — по нему нет ни замеров, ни
  // чисел. Его карточка остаётся доступной из списка брендов, вкладка «Архив».
  const brands = scope.liveBrands;
  const agency = session.organization?.kind === 'agency';

  const groups: MenuGroup[] = [];

  // Организация. Одно членство — выбирать не из чего, и группу мы не рисуем:
  // меню с единственным пунктом только отнимает движение.
  if (session.memberships.length > 1) {
    groups.push({
      key: 'org',
      title: t('switcher.organizations'),
      items: session.memberships.map((membership) => ({
        key: membership.organization_id,
        label: membership.organization_name,
        note: ROLE_NAMES[membership.role],
        selected: membership.organization_id === session.organization?.id,
        onSelect: () => {
          setError(null);
          void switchOrganization(membership.organization_id).catch(setError);
        },
      })),
    });
  }

  const brandItem = (brand: Brand, place: string | null): MenuItem => ({
    key: brand.id,
    label: brand.name,
    note: brand.domain ? domainProse(brand.domain) : t('switcher.no-domain'),
    // По имени клиента ищут не реже, чем по имени бренда: «покажи всё, что у
    // нас по Автодому» — это запрос про клиента.
    search: place ?? undefined,
    selected: brand.id === scope.brandId,
    onSelect: () => scope.selectBrand(brand.id),
  });

  // Бренды по клиентам. Заголовок группы появляется только там, где клиентов
  // больше одного, — у клиента агентства он не появится никогда, он видит
  // ровно своё.
  const grouped = workspaces.length > 1 && role !== 'agency_client';
  const placed = new Set<string>();

  if (grouped) {
    for (const workspace of workspaces) {
      const mine = brands.filter((brand) => brand.workspace_id === workspace.id);
      if (mine.length === 0) continue;
      const place = workspaceTitle(workspace.name, workspace.client_display_name);
      mine.forEach((brand) => placed.add(brand.id));
      groups.push({
        key: workspace.id,
        title: place,
        items: mine.map((brand) => brandItem(brand, place)),
      });
    }
  }

  // Бренд, чьё пространство в список не попало (архивное или дальше сотни),
  // иначе исчез бы из переключателя совсем — а вместе с ним и доступ к своим
  // числам.
  const rest = grouped ? brands.filter((brand) => !placed.has(brand.id)) : brands;
  if (rest.length > 0 || groups.length === 0) {
    groups.push({
      key: 'brands',
      title: grouped ? t('switcher.other-brands') : undefined,
      empty: t('switcher.empty'),
      items: rest.map((brand) => brandItem(brand, null)),
    });
  }

  // Подпись под названием бренда: что отличает его от соседнего. Организация
  // стоит в ней, только если членств несколько, — иначе это одно и то же слово
  // на каждом экране у каждого клиента.
  const place = scope.workspace
    ? workspaceTitle(scope.workspace.name, scope.workspace.client_display_name)
    : null;
  const where = [
    session.memberships.length > 1 ? session.organization?.name : null,
    workspaces.length > 1 && place ? place : null,
    scope.brand?.domain ? domainProse(scope.brand.domain) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Бренд не выбран — вместо примет стоит число, из которого выбирать.
  //
  // Строка счётная, и формы у неё разное число на разных языках: три в русском
  // (`1 бренд`, `2 бренда`, `5 брендов`), две в английском, шесть в арабском.
  // Поэтому она лежит в каталоге целиком, на языке ICU, а форму выбирает
  // `Intl.PluralRules` активной локали — не мы и не тройка слов на месте вызова.
  const label = scope.brand ? where : t('switcher.brand-count', { count: brands.length });

  return (
    <div className="context-slot">
      <Menu
        label={t(agency ? 'switcher.trigger.brand-and-client' : 'switcher.trigger.brand')}
        groups={groups}
        search={
          brands.length >= SEARCH_FROM
            ? t(agency ? 'switcher.search.brands-and-clients' : 'switcher.search.brands')
            : undefined
        }
        trigger={
          <span className="ctx">
            <span className="ctx-name">{scope.brand?.name ?? t('switcher.pick')}</span>
            {label ? <span className="ctx-where">{label}</span> : null}
          </span>
        }
      />

      {error ? (
        <p className="context-error" role="alert">
          {error instanceof ApiError
            ? humanMessage(error.problem)
            : t('switcher.switch-failed')}
        </p>
      ) : null}
    </div>
  );
}
